import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AngularFirestore, AngularFirestoreCollection, AngularFirestoreDocument } from 'angularfire2/firestore';
import { FirestoreService } from './firestore/firestore.service';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/combineLatest';
import 'rxjs/add/operator/mergeMap';
import * as firebase from 'firebase';
import { Upload } from './upload/upload';

import { ProductClientService } from './product-client.service';

import { Globals, Ref } from '../globals';
import { CustomerBillData } from './customer-bill.service';

export interface SupplierBill {
  active: number;
  createdAt: any;
  updatedAt: any;
  
  supplierRef: any;
  fiscalDataRef: any;
  
  date: string;
  folio: string;
  subtotal: number;
  iva: any;
  total: number;
  paymentDate: string;
  retentionIVA: number;
  retentionISR: number;
  
  justifications: Justification[];
  chargeNotes: ChargeNote[];
  
  chargeNoteTotal: number;
  outcomeds: Outcomed[];
  
  currency: keyof CustomerBillData['currencySelect'];
  change: number;
  
  files: File[];
  canceled: boolean;
  cancelReasons: {datetime: string, msg: string}[];
}

export interface File{
  id: string;
  name: string;
  url: string;
}

export interface SupplierBillId extends SupplierBill {
  id: string;
}

export interface Justification {
  concept: string;
  price: number;
  purchaseOrderRef: any;
  expenseConceptRef: Ref | null;
  __prForOrderBilled?: {[prId: string]: number};
}

export interface ChargeNote {
  date: string;
  subtotal: number;
  iva: number;
  total: number;
  notes?: string;
}

export interface Outcomed {
  outcomeRef: any;
  amount: number;
}

@Injectable()
export class SupplierBillService {

  supplierBillCollection: AngularFirestoreCollection<SupplierBill>;
  supplierBills: any;

  supplierBillDocument: AngularFirestoreDocument<SupplierBillId>;
  supplierBill: Observable<SupplierBillId>;

  collection: string;
  supplierCollection: string;
  fiscalDataCollection: string;
  purchaseOrderCollection: string;

  // NOTE: Change this any for SupplierBill Interface
  supplierBill$: Observable<any>;
  supplierBills$: Observable<any[]>;

  constructor(
    private afs: AngularFirestore,
    private afh: FirestoreService,
    private prClientService: ProductClientService,
    private router: Router
  ) {
    this.collection = Globals.SUPPLIER_BILL_COLLECTION;
    this.supplierCollection = Globals.CUSTOMER_SUPPLIER_COLLECTION;
    this.fiscalDataCollection = Globals.FISCAL_DATA_COLLECTION;
    this.purchaseOrderCollection = Globals.PURCHASE_ORDER_COLLECTION;
  }

  /**
   * Get all supplierBills with references
   * @return {Observable} Arrays of supplierBills
   */
  getSupplierBills({date='', year='', onEmpty=null}={}) {
    let col;
    if(date){
      col = this.afh.colWithIds$(this.collection, ref => ref.orderBy('date', 'desc').where('active', '==', true).where('date', '>=', date));
    }else if(year){
      let s = `${year}-00-00`, e = `${year}-99-99`;
      col = this.afh.colWithIds$(this.collection, ref => ref.where('active', '==', true).where('date', '>', s).where('date', '<', e) );
    }else{
      col = this.afh.colWithIds$(this.collection, ref => ref.orderBy('date', 'desc').where('active', '==', true));
    }
    
    return col.map(documents => {
      if(!documents.length && onEmpty) onEmpty();
      return documents.map(document => {
        document.justifications.map(justification => {
          justification.purchaseOrderRef = justification.purchaseOrderRef ? justification.purchaseOrderRef.id : '';
        });

        return Observable.combineLatest(
          this.afh.docWithIds$(`${document.supplierRef.path}`),
          this.afh.docWithIds$(`${document.fiscalDataRef.path}`),
          (supplierData, fiscalDataData) => ({ supplier: supplierData, fiscalData: fiscalDataData })
        ).map( data => ({ supplierObj: data.supplier, fiscalDataObj: data.fiscalData, ...document }) );
      });
    }).mergeMap(documents => Observable.combineLatest(documents));
  }

  /**
   * Get all supplierBills with references whitin 15 days
   * @return {Observable} Arrays of supplierBills
   */
  getSupplierBillsFifteenDays() {
    let filteredDate = new Date();
    filteredDate.setDate(filteredDate.getDate() - 15);
    let date = this.stringifyDate(filteredDate);
    return this.afh.colWithIds$(this.collection, ref => ref.orderBy('date','desc').where('active', '==', true).where('date', '>', date)).map(documents => {
      return documents.map(document => {
        document.justifications.map(justification => {
          justification.purchaseOrderRef = justification.purchaseOrderRef ? justification.purchaseOrderRef.id : '';
        });

        return Observable.combineLatest(
          this.afh.docWithIds$(`${document.supplierRef.path}`),
          this.afh.docWithIds$(`${document.fiscalDataRef.path}`),
          (supplierData, fiscalDataData) => ({ supplier: supplierData, fiscalData: fiscalDataData })
        ).map( data => ({ supplierObj: data.supplier, fiscalDataObj: data.fiscalData, ...document }) );
      });
    }).mergeMap(documents => Observable.combineLatest(documents));
  }

  /**
  * Get supplierBill data
  * @param {string} sid - The id of the supplierBill
  * @return {Observable} The SupplierBill Object
  */
  getSupplierBill(sid: string) {
    return this.supplierBill$ = this.afh.docWithIds$(this.collection + '/' + sid).map(document => {
      document.justifications.map(justification => {
        justification.purchaseOrderRef = justification.purchaseOrderRef ? justification.purchaseOrderRef.id : '';
      });
  
      return Observable.combineLatest(
        this.afh.docWithIds$(`${document.supplierRef.path}`),
        this.afh.docWithIds$(`${document.fiscalDataRef.path}`),
        (supplierData, fiscalDataData) => ({ supplier: supplierData, fiscalData: fiscalDataData })
      ).map( data => ({ supplierObj: data.supplier, fiscalDataObj: data.fiscalData, ...document }) );

    }).mergeMap(documents => Observable.combineLatest(documents));
  }

  /**
  * Create new supplierBill
  * @param {SupplierBill} supplierBill - The data of the supplierBill
  */
  addSupplierBill({...supplierBill}: SupplierBill) {
    supplierBill.supplierRef = this.afh.doc(`${this.supplierCollection}/${supplierBill.supplierRef}`).ref;
    supplierBill.fiscalDataRef = this.afh.doc(`${this.fiscalDataCollection}/${supplierBill.fiscalDataRef}`).ref;
    supplierBill.justifications = supplierBill.justifications.map(({purchaseOrderRef, ...justification}) => ({
      purchaseOrderRef: purchaseOrderRef ? this.afh.doc(`${this.purchaseOrderCollection}/${purchaseOrderRef}`).ref : '',
      ...justification
    }) );
    this.afh.add(`${this.collection}`, supplierBill);
  }

  /**
   * Update current supplierBill
   * @param {string} sid  - The id of the supplierBill
   * @param {SupplierBill} supplierBill - The data of the supplierBill
   */
  updateSupplierBill(sid: string, {...supplierBill}: SupplierBill) {
    supplierBill.supplierRef = this.afh.doc(`${this.supplierCollection}/${supplierBill.supplierRef}`).ref;
    supplierBill.fiscalDataRef = this.afh.doc(`${this.fiscalDataCollection}/${supplierBill.fiscalDataRef}`).ref;
    supplierBill.justifications = supplierBill.justifications.map(({purchaseOrderRef, ...justification}) => ({
      purchaseOrderRef: purchaseOrderRef ? this.afh.doc(`${this.purchaseOrderCollection}/${purchaseOrderRef}`).ref : '',
      ...justification
    }) );
    this.afh.update(`${this.collection}/${sid}`, supplierBill);
  }

  updateByField(object: any, id: string){
    this.afh.update(`${this.collection}/${id}`, object);
  }

  async folioExistsForSupplier(folio: string, supplierId: string): Promise<boolean>{
    return new Promise<boolean>(resolve => {
      let supplierRef: Ref = this.afh.doc(`${this.supplierCollection}/${supplierId}`).ref;
      let sub = this.afh.colWithIds$(this.collection, ref => ref.where('active', '==', true).where('folio', '==', folio).where('supplierRef', '==', supplierRef)).subscribe(bills => {
        sub.unsubscribe();
        resolve(!!bills.length);
      });
    });
  }

  get afhService(){
    return this.afh;
  }

  /**
   * Delete current supplierBill
   * @param {string} sid - The id of the supplierBill
   */
  deleteSupplierBill(sid: string) {
    this.afh.delete(`${this.collection}/${sid}`);
  }

  windowReplace(action: string, id?: string){
    switch(action){
      case 'add':
      case 'create':
        this.router.navigate([`/new_supplierBill/${id}`]);
        break;
      case 'edit':
      case 'update':
        this.router.navigate([`/edit_supplierBill/${id}`]);
        break;
      case 'view':
      case 'display':
        this.router.navigate([`/vw_supplierBill/${id}`]);
        break;
      case 'read':
        this.router.navigate([`/supplierBill`]);
        break;
      default:
        console.log('Not an action');
    }
  }

  defaultChargeNote: ChargeNote = {
    date: this.today,
    subtotal: 0,
    iva: 0,
    total: 0
  }


  get today(){
    return this.stringifyDate(new Date());
  }
  stringifyDate(date: Date){
    var dd:string = date.getDate() + '';
    var mm:string = date.getMonth()+ 1 + '';
    var yyyy = date.getFullYear();
    if(+dd<10) dd = '0'+dd;
    if(+mm<10) mm = '0'+mm;
    return yyyy + '-' + mm + '-' + dd;
  }



  uploadFile(upload: Upload, supplierBill: SupplierBillId, fileName: string) {
    const id = supplierBill.id;
    let files = supplierBill.files || [];
    const fileId = `${this.makeFileId}_${files.length}_${new Date().getTime()}`;
    const storageRef = firebase.storage().ref();
    const uploadTask = storageRef.child(`${this.collection}/${id}/${fileId}`).put(upload.file);
    uploadTask.on(firebase.storage.TaskEvent.STATE_CHANGED, (snapshot: any) => {
      upload.progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
    }, (err) => {
      console.log('error al subir');
    }, () => {
      files.push({
        id: fileId,
        name: fileName,
        url: uploadTask.snapshot.downloadURL
      });
      this.afh.update(`${this.collection}/${id}`, {files});
    });
  }
  deleteFile(supplierBill: SupplierBillId, index){
    const id = supplierBill.id;
    const fileId = supplierBill.files[index].name;
    const storageRef = firebase.storage().ref();
    var imageToDeleteRef = storageRef.child(`${this.collection}/${id}/${fileId}`);
    imageToDeleteRef.delete();
    supplierBill.files.splice(index, 1);
    this.afh.update(`${this.collection}/${id}`, {files: supplierBill.files});
  }
  get makeFileId(){
      var text = "";
      var possibleL = "ABCDEFGHIJKLMNPQRSTUVWXYZabcdefghijklmnpqrstuvwxyz";
      for (var i = 0; i < 11; i++) text += possibleL.charAt(Math.floor(Math.random() * possibleL.length));
    return text;
  }
}

export class SupplierBillData {
  justificationCategorySelect = [
    {name: 'Seleccione una opción', value: ''},
    //{name: 'Orden de compra', value: 'Orden de compra'},
    //{name: 'Gastos de venta', value: 'Gastos de venta'},
    //{name: 'Gastos de administración', value: 'Gastos de administración'},
    //{name: 'Gastos de producción', value: 'Gastos de producción'},
    //{name: 'Gastos de dirección', value: 'Gastos de dirección'},
    //{name: 'Otros gastos', value: 'Otros gastos'},
  ];
}

export class SupplierBillFilter {
  allRows: any[];

  dateIndex: number;
  supplierIndex: number;
  completionIndex: number;

  constructor(allRows: any[], {dateIndex=0, supplierIndex=0, completionIndex=0}){
    this.allRows = allRows;
    this.dateIndex = dateIndex;
    this.supplierIndex = supplierIndex;
    this.completionIndex = completionIndex;

    let suppliers = new Set( allRows.map(row => row.supplierName) );
    let sortedSuppliers = [...suppliers].sort((it1, it2) => it1 > it2 ? 1 : -1);
    this.supplierSelect = ['Todos', ...sortedSuppliers].map(name => ({name}) );
  }

  get filteredData() {
    return this.allRows
      .filter(({date}) => {
        let [py, pm, pd] = date.split('-');
        let rowDate = new Date(py, pm - 1, pd);
        let filterDate = new Date();
        let {amount, unit} = this.dateSelect[this.dateIndex];
        switch(unit){
          case 'day': filterDate.setDate(filterDate.getDate() - amount); break;
          case 'month': filterDate.setMonth(filterDate.getMonth() - amount); filterDate.setDate(0); break;
          case 'year': filterDate = new Date(filterDate.getFullYear() - amount, 0); break;
          default: filterDate.setFullYear(2015); break;
        }
        if(rowDate >= filterDate) return 1;
      })
      .filter(({supplierName: rowName}) => {
        let {name: filterName} = this.supplierSelect[this.supplierIndex];
        if(rowName == filterName || filterName == 'Todos') return 1;
      })
      .filter(({residue}) => {
        let {name} = this.completionSelect[this.completionIndex];
        if( ( name == 'Todas' ) || ( name == 'Completadas' && residue == 0 ) || ( name == 'No completadas' && residue > 0 ) ) return 1;
      });
  }

  get json(): string {
    return JSON.stringify({
      dateIndex: this.dateIndex,
      supplierIndex: this.supplierIndex,
      completionIndex: this.completionIndex
    });
  }

  dateSelect = [
    { amount: 15, unit: 'day', name: '15 días' },
    { amount: 0, unit: 'month', name: 'Este mes' },
    { amount: 6, unit: 'month', name: '6 meses' },
    { amount: 0, unit: 'year', name: 'Este año' },
    { amount: 0, unit: 'forever', name: 'Siempre' },
  ];
  supplierSelect = [];
  completionSelect = [
    { name: 'Todas' },
    { name: 'Completadas' },
    { name: 'No completadas' },
  ]
}
